import { Box, Flex, Grid, Icon, Spinner, Text, useColorModeValue } from '@chakra-ui/react'
import axios from 'axios'
import { useEffect, useState } from 'react'
import { FiCheckCircle, FiEye, FiUsers } from 'react-icons/fi'
import { useNavigate } from 'react-router-dom'
import { API_URL } from '../../api/config'

const AnimatedCounter = ({ value }: { value: number }) => {
  const [displayValue, setDisplayValue] = useState(0);
  const textColor = useColorModeValue('gray.800', 'white');

  useEffect(() => {
    const increment = value / 50; // 50 frames para suavizar
    let current = 0;
    const timer = setInterval(() => {
      current += increment;
      if (current >= value) {
        setDisplayValue(value);
        clearInterval(timer);
      } else {
        setDisplayValue(Math.floor(current));
      }
    }, 20);
    return () => clearInterval(timer);
  }, [value]);

  return (
    <Text fontSize={{ base: 'lg', md: 'xl' }} fontWeight="bold" color={textColor}>
      {displayValue.toLocaleString()}
    </Text>
  );
};

const StatsCounters = ({ selectedEvent }: { selectedEvent: string }) => {
  const [stats, setStats] = useState<{ totalRegistrations: number; totalCheckins: number; totalCheckinsToday: number; totalAccesses: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchStats = async () => {
      // if (!selectedEvent) return;
      setLoading(true);
      try {
        const response = await axios.get(`${API_URL}registration/stats`, {
        // const response = await axios.get(`${API_URL}registration/stats?eventId=${selectedEvent}`, {
          headers: { 'Content-Type': 'application/json' }
        });
        setStats(response.data);
      } catch (error) {
        console.error('Erro ao buscar dados:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [selectedEvent]);

  const handleAccessClick = () => {
    navigate('/admin/access-list');
  };

  const handleRegistrationClick = () => {
    navigate('/admin/registration-list');
  };

  if (loading) {
    return (
      <Flex justify="center" align="center" minHeight="150px">
        <Spinner size="xl" color="teal.500" />
      </Flex>
    );
  }

  return (
    <Grid templateColumns={{ base: '1fr', md: 'repeat(4, 1fr)' }} gap={6} mb={10}>
      <StatCard 
        icon={FiUsers} 
        color="blue.400" 
        label="Total de Registros" 
        value={stats?.totalRegistrations ?? 0}
        onClick={handleRegistrationClick}
        isClickable={true}
      />
      <StatCard icon={FiCheckCircle} color="green.400" label="Total de Check-ins" value={stats?.totalCheckins ?? 0} />
      <StatCard icon={FiCheckCircle} color="teal.400" label="Total de Check-ins hoje" value={stats?.totalCheckinsToday ?? 0} />
      <StatCard
        icon={FiEye}
        color="orange.400"
        label="Total de Acessos"
        value={stats?.totalAccesses ?? 0}
        onClick={handleAccessClick}
        isClickable={true}
      />
    </Grid>
  );
};

const StatCard = ({
  icon,
  color,
  label,
  value,
  onClick,
  isClickable = false
}: {
  icon: any;
  color: string;
  label: string;
  value: number;
  onClick?: () => void;
  isClickable?: boolean;
}) => {
  const bgColor = useColorModeValue('white', 'gray.800');

  return (
    <Flex
      direction="row"
      bg={bgColor}
      p={5}
      rounded="xl"
      shadow="md"
      align="center"
      justify="space-between"
      _hover={{
        shadow: 'lg',
        transform: isClickable ? 'scale(1.02)' : 'scale(1.01)',
        cursor: isClickable ? 'pointer' : 'default'
      }}
      transition="all 0.2s"
      onClick={isClickable ? onClick : undefined}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyPress={isClickable ? (e) => e.key === 'Enter' && onClick?.() : undefined}
    >
      <Flex bg={color} p={4} rounded="full" align="center" justify="center">
        <Icon as={icon} boxSize={{ base: 8, md: 10 }} color="white" />
      </Flex>
      <Box textAlign="right">
        <AnimatedCounter value={value} />
        <Text fontSize={{ base: 'xs', md: 'sm' }} color="gray.500">
          {label}
        </Text>
        {isClickable && (
          <Text fontSize="xs" color="blue.500" lineHeight="1.2">
            (clique para ver detalhes)
          </Text>
        )}
      </Box>
    </Flex>
  );
};

export default StatsCounters;
